import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { api, formatDate } from "../lib/api";
import { CATEGORIES } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, MaskedLine } from "../components/Reveal";

export default function BlogPage() {
  const { t, lang } = useLanguage();
  const [articles, setArticles] = useState(null);
  const [category, setCategory] = useState("Todos");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/articles", { params: { lang } }).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, [lang]);

  const filtered = useMemo(() => {
    if (!articles) return [];
    const q = query.trim().toLowerCase();
    return articles.filter(
      (a) =>
        (category === "Todos" || a.category === category) &&
        (!q || `${a.title} ${a.excerpt}`.toLowerCase().includes(q))
    );
  }, [articles, category, query]);

  return (
    <main data-testid="blog-page" className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-14">
          <p className="overline-label mb-6">{t("blog.overline")}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight text-ebony" data-testid="blog-title">
            <MaskedLine delay={0.1}>{t("blog.title1")}</MaskedLine>
            <MaskedLine delay={0.25}><em className="italic text-forest">{t("blog.title2")}</em></MaskedLine>
          </h1>
          <p className="text-base text-[#4D5248] leading-relaxed max-w-xl mt-8">
            {t("blog.intro")}
          </p>
        </header>

        <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between border-y border-[#E2E4DC] py-6 mb-14">
          <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
            {CATEGORIES.map((c, i) => (
              <button
                key={c}
                data-testid={`blog-category-filter-${i}`}
                onClick={() => setCategory(c)}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors duration-300 ${
                  category === c
                    ? "bg-forest text-alabaster border-forest"
                    : "border-[#E2E4DC] text-[#6C7067] hover:border-olive hover:text-forest"
                }`}
              >
                {t(`cat.${c}`)}
              </button>
            ))}
          </div>
          <div className="relative lg:w-72">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA096]" />
            <input
              data-testid="blog-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("blog.search")}
              className="input-field !pl-11"
            />
          </div>
        </div>

        {articles === null ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#9AA096] py-20 text-center">
            {t("blog.loading")}
          </p>
        ) : filtered.length === 0 ? (
          <div className="border border-[#E2E4DC] bg-mist/60 p-12 sm:p-20 text-center" data-testid="blog-empty-state">
            <p className="font-serif italic text-2xl sm:text-3xl text-forest mb-4">
              {articles.length === 0 ? t("blog.emptyPrepTitle") : t("blog.emptyNoneTitle")}
            </p>
            <p className="text-sm text-[#757A70] max-w-md mx-auto leading-relaxed">
              {articles.length === 0 ? t("blog.emptyPrep") : t("blog.emptyNone")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
            {filtered.map((a, i) => (
              <Reveal key={a.id} delay={0.05 * (i % 3)} className="h-full">
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-article-card-${i}`}
                  className="group flex flex-col h-full bg-white border border-[#E2E4DC] hover:border-olive/50 transition-colors duration-500"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-sage">
                    {a.cover_image ? (
                      <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full grid place-items-center">
                        <span className="font-serif italic text-5xl text-olive/40">§</span>
                      </div>
                    )}
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">
                      {t(`cat.${a.category}`)} · {formatDate(a.created_at, lang)}
                    </p>
                    <h2 className="font-serif text-xl font-medium text-ebony leading-snug group-hover:text-forest transition-colors">
                      {a.title}
                    </h2>
                    <p className="text-sm text-[#757A70] leading-relaxed mt-3 line-clamp-3 flex-1">{a.excerpt}</p>
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-forest mt-6 group-hover:text-gold transition-colors">
                      {t("blog.readMore")} · {a.read_time} min <ArrowUpRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
